<script>
  import { untrack } from 'svelte';
  import {
    studioSettings,
    studioSettingsUI,
    applyStudioSettings,
    resetStudioSettings,
    closeStudioSettings,
    toggleStudioSettings,
    DEFAULT_SELECTED_MODELS,
    ASPECT_RATIOS,
    ASPECT_RATIO_ORDER,
    clampDimension,
    modelRequiresMatureContent
  } from '../state/settings.svelte.js';
  import { auth } from '../state/auth.svelte.js';
  import MatureContentSwitch from './MatureContentSwitch.svelte';
  import ImageModelPicker from './studio/ImageModelPicker.svelte';
  import ImageReferencesPanel from './studio/ImageReferencesPanel.svelte';
  import ModelParameterControls from './studio/ModelParameterControls.svelte';
  import { imageModels } from '../state/model-catalog.svelte.js';
  import { getImageSizeErrors } from '../utils/image-model-parameters.js';
  import RenderPresetControl from './studio/RenderPresetControl.svelte';
  import RenderSelectionSummary from './studio/RenderSelectionSummary.svelte';
  import { focusDialog } from '../utils/dialog-focus.js';

  let { promptCount = 1 } = $props();

  const batchRenderOptions = [1, 2, 4, 8, 12, 16];

  let settingsButtonRef = $state(null);
  let settingsPopoverRef = $state(null);
  let showSaveToast = $state(false);
  let settingsDraft = $state({
    batchRenders: studioSettings.batchRenders,
    promptStylesAlike: studioSettings.promptStylesAlike,
    selectedModels: [...studioSettings.selectedModels],
    network: studioSettings.network,
    renderPreset: studioSettings.renderPreset,
    modelOverrides: { ...studioSettings.modelOverrides },
    matureContentEnabled: studioSettings.matureContentEnabled,
    matureContentConfirmed: studioSettings.matureContentConfirmed,
    // Image size settings
    aspectRatio: studioSettings.aspectRatio,
    resolution: studioSettings.resolution,
    useCustomSize: studioSettings.useCustomSize,
    customWidth: studioSettings.customWidth,
    customHeight: studioSettings.customHeight
  });

  // Computed: get current dimensions based on draft settings
  function getDraftDimensions() {
    if (settingsDraft.useCustomSize) {
      return { width: settingsDraft.customWidth, height: settingsDraft.customHeight };
    }
    const ratio = ASPECT_RATIOS[settingsDraft.aspectRatio] || ASPECT_RATIOS['1:1'];
    const dims = settingsDraft.resolution === 'hd' ? ratio.hd : ratio.standard;
    return { width: dims.w, height: dims.h };
  }
  const sizeErrors = $derived(getImageSizeErrors(
    imageModels.filter(model => settingsDraft.selectedModels.includes(model.id)), getDraftDimensions()
  ));

  // Swap width and height
  function swapDimensions() {
    if (settingsDraft.useCustomSize) {
      const temp = settingsDraft.customWidth;
      settingsDraft.customWidth = settingsDraft.customHeight;
      settingsDraft.customHeight = temp;
    } else {
      // Find the inverse aspect ratio
      const currentRatio = settingsDraft.aspectRatio;
      const inverseMap = {
        '9:16': '16:9', '16:9': '9:16',
        '2:3': '3:2', '3:2': '2:3',
        '3:4': '4:3', '4:3': '3:4',
        '1:1': '1:1'
      };
      settingsDraft.aspectRatio = inverseMap[currentRatio] || currentRatio;
    }
  }

  // Handle custom dimension input
  function handleCustomWidthChange(e) {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      settingsDraft.customWidth = clampDimension(value);
    }
  }

  function handleCustomHeightChange(e) {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      settingsDraft.customHeight = clampDimension(value);
    }
  }

  $effect(() => {
    if (!studioSettingsUI.isOpen) return;
    // Re-seed the draft from committed settings ONLY on the open transition.
    // Read studioSettings untracked: otherwise an immediate mutation while the
    // panel is open (e.g. the Mature Content toggle persisting via
    // setMatureContentPreference) would re-run this effect and silently
    // overwrite the user's unsaved model reorder / enable / size edits.
    untrack(() => {
      settingsDraft.batchRenders = studioSettings.batchRenders;
      settingsDraft.promptStylesAlike = studioSettings.promptStylesAlike;
      settingsDraft.selectedModels = [...studioSettings.selectedModels];
      settingsDraft.network = studioSettings.network;
      settingsDraft.renderPreset = studioSettings.renderPreset;
      settingsDraft.modelOverrides = { ...studioSettings.modelOverrides };
      settingsDraft.matureContentEnabled = studioSettings.matureContentEnabled;
      settingsDraft.matureContentConfirmed = studioSettings.matureContentConfirmed;
      // Image size settings
      settingsDraft.aspectRatio = studioSettings.aspectRatio;
      settingsDraft.resolution = studioSettings.resolution;
      settingsDraft.useCustomSize = studioSettings.useCustomSize;
      settingsDraft.customWidth = studioSettings.customWidth;
      settingsDraft.customHeight = studioSettings.customHeight;
    });
  });

  function handleSaveSettings() {
    applyStudioSettings({
      batchRenders: Number(settingsDraft.batchRenders),
      promptStylesAlike: settingsDraft.promptStylesAlike,
      selectedModels: settingsDraft.selectedModels,
      network: settingsDraft.network,
      renderPreset: settingsDraft.renderPreset,
      modelOverrides: settingsDraft.modelOverrides,
      matureContentEnabled: settingsDraft.matureContentEnabled,
      matureContentConfirmed: settingsDraft.matureContentConfirmed,
      // Image size settings
      aspectRatio: settingsDraft.aspectRatio,
      resolution: settingsDraft.resolution,
      useCustomSize: settingsDraft.useCustomSize,
      customWidth: settingsDraft.customWidth,
      customHeight: settingsDraft.customHeight
    });

    showSaveToast = true;
    setTimeout(() => showSaveToast = false, 2000);
  }

  function handleResetSettings() {
    resetStudioSettings();
    settingsDraft.batchRenders = studioSettings.batchRenders;
    settingsDraft.promptStylesAlike = studioSettings.promptStylesAlike;
    settingsDraft.selectedModels = [...studioSettings.selectedModels];
    settingsDraft.network = studioSettings.network;
    settingsDraft.renderPreset = studioSettings.renderPreset;
    settingsDraft.modelOverrides = { ...studioSettings.modelOverrides };
    settingsDraft.matureContentEnabled = studioSettings.matureContentEnabled;
    settingsDraft.matureContentConfirmed = studioSettings.matureContentConfirmed;
    // Image size settings
    settingsDraft.aspectRatio = studioSettings.aspectRatio;
    settingsDraft.resolution = studioSettings.resolution;
    settingsDraft.useCustomSize = studioSettings.useCustomSize;
    settingsDraft.customWidth = studioSettings.customWidth;
    settingsDraft.customHeight = studioSettings.customHeight;
  }

  // The Mature Content switch persists itself (no Save needed); keep the draft
  // in step and drop any 🔞 models from the draft when it turns off.
  function handleMatureChange(enabled) {
    settingsDraft.matureContentEnabled = enabled;
    settingsDraft.matureContentConfirmed = studioSettings.matureContentConfirmed;
    if (!enabled) {
      const next = settingsDraft.selectedModels.filter((id) => !modelRequiresMatureContent(id));
      settingsDraft.selectedModels = next.length ? next : [...DEFAULT_SELECTED_MODELS];
    }
  }

  // Check if there are unsaved changes
  function hasUnsavedChanges() {
    // Check batch renders
    if (settingsDraft.batchRenders !== studioSettings.batchRenders) {
      return true;
    }

    // Check prompt styles
    if (settingsDraft.promptStylesAlike !== studioSettings.promptStylesAlike) {
      return true;
    }

    // Check selected models order
    const currentEnabledModels = settingsDraft.selectedModels;

    if (currentEnabledModels.length !== studioSettings.selectedModels.length) {
      return true;
    }

    for (let i = 0; i < currentEnabledModels.length; i++) {
      if (currentEnabledModels[i] !== studioSettings.selectedModels[i]) {
        return true;
      }
    }

    if (settingsDraft.network !== studioSettings.network) return true;
    if (settingsDraft.renderPreset !== studioSettings.renderPreset) return true;
    if (JSON.stringify(settingsDraft.modelOverrides) !== JSON.stringify(studioSettings.modelOverrides)) return true;

    // Check image size settings
    if (settingsDraft.aspectRatio !== studioSettings.aspectRatio) return true;
    if (settingsDraft.resolution !== studioSettings.resolution) return true;
    if (settingsDraft.useCustomSize !== studioSettings.useCustomSize) return true;
    if (settingsDraft.customWidth !== studioSettings.customWidth) return true;
    if (settingsDraft.customHeight !== studioSettings.customHeight) return true;

    return false;
  }

  function promptToSaveChanges() {
    return confirm('You have unsaved changes. Do you want to save them before closing?');
  }

  function handleClose() {
    if (hasUnsavedChanges()) {
      const shouldSave = promptToSaveChanges();
      if (shouldSave) {
        handleSaveSettings();
      } else {
        closeStudioSettings();
      }
    } else {
      closeStudioSettings();
    }
  }

  function handleWindowClick(event) {
    if (!studioSettingsUI.isOpen) return;
    // Removing a source-image tile detaches the clicked button before this
    // window handler runs. The dispatch path still identifies its dialog.
    const path = event.composedPath();
    if (path.includes(settingsPopoverRef) || path.includes(settingsButtonRef)) {
      return;
    }
    handleClose();
  }

</script>

<svelte:window onclick={handleWindowClick} />

<div class="studio-settings-anchor">
  <div class="settings-wrapper">
    <button
      class="btn-ghost settings-button"
      bind:this={settingsButtonRef}
      onclick={toggleStudioSettings}
      aria-label="Studio settings"
      title="Studio settings"
    >
      SETTINGS
    </button>

    {#if studioSettingsUI.isOpen}
      <div
        class="settings-popover"
        role="dialog"
        aria-label="Studio settings"
        aria-modal="true"
        tabindex="-1"
        bind:this={settingsPopoverRef}
        use:focusDialog={{ initialFocus: 'dialog', onClose: handleClose }}
      >
        <div class="settings-header">
          <h3>Studio Settings</h3>
          <button
            class="btn-ghost btn-icon close-button"
            onclick={handleClose}
            aria-label="Close settings"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div class="settings-body">
          <p class="field-hint">Settings apply to new rounds. Review saved retries separately.</p>
          <RenderPresetControl bind:preset={settingsDraft.renderPreset} bind:imagesPerModel={settingsDraft.batchRenders} readonly={!auth.isLoggedIn} />
          <RenderSelectionSummary selectedIds={settingsDraft.selectedModels} {promptCount} imagesPerModel={settingsDraft.batchRenders} />
          {#if !auth.isLoggedIn}
            <div class="signed-out-banner">
              <span class="signed-out-banner-icon">🔒</span>
              <span class="signed-out-banner-text">Sign in to choose models, sizes, and settings — images render on your own Sogni account</span>
            </div>
          {/if}

          <!-- Image Size Section -->
          <div class="size-section">
            <div class="field-label">Image Size</div>

            <!-- Aspect Ratio Pills -->
            <div class="aspect-ratio-row" class:disabled-section={!auth.isLoggedIn}>
              {#each ASPECT_RATIO_ORDER as ratio}
                {@const ratioData = ASPECT_RATIOS[ratio]}
                {@const isSelected = settingsDraft.aspectRatio === ratio && !settingsDraft.useCustomSize}
                <button
                  type="button"
                  class="aspect-pill"
                  class:selected={isSelected}
                  onclick={() => { if (auth.isLoggedIn) { settingsDraft.aspectRatio = ratio; settingsDraft.useCustomSize = false; } }}
                  title="{ratioData.label}"
                  disabled={!auth.isLoggedIn}
                >
                  <div class="aspect-icon" style="aspect-ratio: {ratio.replace(':', '/')}"></div>
                  <span class="aspect-label">{ratioData.label}</span>
                </button>
              {/each}
            </div>

            <!-- Resolution Toggle -->
            <div class="resolution-row" class:disabled-section={!auth.isLoggedIn}>
              <button
                type="button"
                class="resolution-btn"
                class:selected={settingsDraft.resolution === 'standard' && !settingsDraft.useCustomSize}
                onclick={() => { if (auth.isLoggedIn) { settingsDraft.resolution = 'standard'; settingsDraft.useCustomSize = false; } }}
                disabled={!auth.isLoggedIn}
              >
                <span class="res-label">Standard</span>
                <span class="res-hint">~512px</span>
              </button>
              <button
                type="button"
                class="resolution-btn"
                class:selected={settingsDraft.resolution === 'hd' && !settingsDraft.useCustomSize}
                onclick={() => { if (auth.isLoggedIn) { settingsDraft.resolution = 'hd'; settingsDraft.useCustomSize = false; } }}
                disabled={!auth.isLoggedIn}
              >
                <span class="res-label">HD</span>
                <span class="res-hint">~1024px</span>
              </button>
            </div>

            <!-- Preview & Dimensions -->
            {#if true}
              {@const dims = getDraftDimensions()}
              {@const isPortrait = dims.height > dims.width}
              {@const aspectRatioValue = dims.width / dims.height}
              {@const isSquare = dims.width === dims.height}
              <div class="size-preview-row">
                <div class="preview-box-container">
                  <div
                    class="preview-box"
                    style="aspect-ratio: {aspectRatioValue}; {isPortrait ? 'height: 48px' : 'width: 48px'}"
                  ></div>
                </div>
                <div class="dimension-display">
                  <span class="dim-value">{dims.width} × {dims.height}</span>
                  {#if !isSquare && auth.isLoggedIn}
                    <button
                      type="button"
                      class="swap-btn"
                      onclick={swapDimensions}
                      title="Swap width and height"
                    >
                      ↔
                    </button>
                  {/if}
                </div>
              </div>
            {/if}

            <!-- Custom Size Toggle -->
            {#if auth.isLoggedIn}
              <label class="custom-toggle">
                <input
                  type="checkbox"
                  bind:checked={settingsDraft.useCustomSize}
                />
                <span>Custom size</span>
              </label>
            {/if}

            <!-- Custom Size Inputs (shown when enabled and logged in) -->
            {#if settingsDraft.useCustomSize && auth.isLoggedIn}
              <div class="custom-inputs">
                <div class="custom-field">
                  <label for="custom-width">Width</label>
                  <input
                    type="number"
                    id="custom-width"
                    value={settingsDraft.customWidth}
                    onchange={handleCustomWidthChange}
                    min="256"
                    max="3840"
                    step="8"
                  />
                </div>
                <span class="dimension-separator">×</span>
                <div class="custom-field">
                  <label for="custom-height">Height</label>
                  <input
                    type="number"
                    id="custom-height"
                    value={settingsDraft.customHeight}
                    onchange={handleCustomHeightChange}
                    min="256"
                    max="3840"
                    step="8"
                  />
                </div>
              </div>
              <p class="field-hint">Valid range: 256–3840px; each model's supported range is checked before rendering</p>
            {/if}
            {#each sizeErrors as error (error.modelId)}
              <p class="field-hint" role="status">{error.message} Choose a supported size, such as HD, or change the selected model.</p>
            {/each}
          </div>

          <div class="section-divider"></div>

          <div class="field-label">Image Models:</div>
          <ImageModelPicker bind:selectedIds={settingsDraft.selectedModels}
            bind:network={settingsDraft.network}
            matureContentEnabled={settingsDraft.matureContentEnabled}
            readonly={!auth.isLoggedIn} />
          <ImageReferencesPanel selectedIds={settingsDraft.selectedModels} readonly={!auth.isLoggedIn}
            onMatchSize={(dimensions) => {
              settingsDraft.useCustomSize = true;
              settingsDraft.customWidth = dimensions.width;
              settingsDraft.customHeight = dimensions.height;
            }} />
          <ModelParameterControls selectedIds={settingsDraft.selectedModels}
            preset={settingsDraft.renderPreset} bind:overrides={settingsDraft.modelOverrides} readonly={!auth.isLoggedIn} />

          {#if auth.isLoggedIn}
            <div class="section-divider"></div>
            <MatureContentSwitch onChange={handleMatureChange} />
            <div class="section-divider"></div>
          {/if}

          <label for="batch-render-count">Images per model:</label>
          <select
            id="batch-render-count"
            bind:value={settingsDraft.batchRenders}
          >
            {#each batchRenderOptions as option}
              <option value={option}>{option}</option>
            {/each}
          </select>
          <p class="field-hint">Controls how many images to generate per model (1–16).</p>

          <label for="prompt-style-input">Default Style:</label>
          <textarea
            id="prompt-style-input"
            rows="3"
            placeholder="e.g. intricate details, breathtaking, cinematic still"
            bind:value={settingsDraft.promptStylesAlike}
          ></textarea>
          <p class="field-hint">Appended to every prompt. Clear this field to turn it off.</p>
        </div>

        <div class="settings-footer">
          <a
            class="footer-docs-link"
            href="https://docs.sogni.ai/sogni-vibe/"
            target="_blank"
            rel="noopener"
          >
            Docs ↗
          </a>
          <div class="spark-info">
            <div class="spark-tier">🆓 Free Spark · ⚡ Premium Spark</div>
          </div>
        </div>

        <div class="settings-actions">
          {#if showSaveToast}
            <span class="save-toast">Settings saved</span>
          {/if}
          <button class="btn-secondary" type="button" onclick={handleResetSettings}>
            Reset
          </button>
          <button class="btn-primary" type="button" onclick={handleSaveSettings}>
            Save
          </button>
        </div>
      </div>
    {/if}
  </div>
</div>

<style>
  .studio-settings-anchor {
    position: relative;
    z-index: 200;
  }

  .settings-wrapper {
    position: relative;
  }

  .settings-button {
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    line-height: 1;
    padding: 0;
    color: var(--color-text-muted);
    border: none;
    background: none;
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .settings-button:hover {
    color: var(--color-primary);
  }

  .settings-popover {
    position: absolute;
    top: calc(100% + 0.5rem);
    right: 0;
    width: min(380px, calc(100vw - 2rem));
    max-height: calc(100vh - 6rem);
    overflow-y: auto;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-lg);
    padding: 1rem;
    animation: fadeIn 0.15s ease;
  }

  .settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .settings-header h3 {
    margin: 0 0 0.875rem 0;
    font-size: 1rem;
    color: var(--color-primary);
    text-align: left;
    font-weight: 600;
  }

  .close-button {
    font-size: 1.25rem;
    line-height: 1;
    padding: 0.25rem 0.5rem;
    margin-bottom: 0.875rem;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  .close-button:hover {
    opacity: 1;
  }

  .settings-body {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  /* Signed-out banner styles */
  .signed-out-banner {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem 1rem;
    background: rgba(255, 27, 141, 0.08);
    border: 1px solid rgba(255, 27, 141, 0.25);
    border-radius: var(--radius);
    margin-bottom: 0.5rem;
  }

  .signed-out-banner-icon {
    font-size: 1rem;
    line-height: 1;
  }

  .signed-out-banner-text {
    font-size: 0.8125rem;
    color: var(--color-text);
    line-height: 1.4;
  }

  /* Disabled section styles for signed-out users */
  .disabled-section {
    opacity: 0.5;
    pointer-events: none;
  }



  .settings-body label,
  .settings-body .field-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-text);
    line-height: 1.3;
  }

  .settings-body select,
  .settings-body textarea {
    width: 100%;
    font-size: 0.875rem;
  }

  .settings-body textarea {
    resize: vertical;
    min-height: 70px;
    line-height: 1.4;
  }










  /* Highlight drag handle on touch */












  .field-hint {
    margin: -0.5rem 0 0 0;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    line-height: 1.4;
  }

  .settings-footer {
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .footer-docs-link {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    text-decoration: none;
  }

  .footer-docs-link:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }



  .settings-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--color-border);
  }

  .save-toast {
    font-size: 0.8125rem;
    color: #28a745;
    font-weight: 600;
    animation: fadeIn 0.2s ease;
  }

  .settings-actions button {
    flex: 1;
    min-width: 0;
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: translateY(-4px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Mobile-specific adjustments */
  @media (max-width: 640px) {
    .settings-popover {
      /* Full width on mobile */
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
      max-height: none;
      border-radius: 0;
      border: none;
      overflow-y: auto;
      z-index: 1000;
    }

    .settings-header {
      position: sticky;
      top: 0;
      background: var(--color-surface);
      padding: 1rem 0 0.5rem 0;
      margin: -1rem -1rem 0 -1rem;
      padding: 1rem;
      z-index: 10;
      border-bottom: 1px solid var(--color-border);
    }

    .settings-header h3 {
      font-size: 1.125rem;
      margin-bottom: 0;
    }

    .close-button {
      margin-bottom: 0;
    }

    .settings-body {
      padding-top: 0.5rem;
    }

    .settings-actions {
      position: sticky;
      bottom: 0;
      background: var(--color-surface);
      margin: 0 -1rem -1rem -1rem;
      padding: 0.75rem 1rem;
      border-top: 1px solid var(--color-border);
    }



  }

  /* Tablet and up */
  @media (min-width: 641px) {
    .settings-popover {
      width: 420px;
      max-height: calc(100vh - 5rem);
    }

    .settings-actions button {
      flex: 0 1 auto;
      min-width: 90px;
    }
  }

  /* Image Size Section Styles */
  .size-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .section-divider {
    height: 1px;
    background: var(--color-border);
    margin: 0.5rem 0;
  }

  .aspect-ratio-row {
    display: flex;
    gap: 0.375rem;
    flex-wrap: wrap;
  }

  .aspect-pill {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.625rem;
    border: 2px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    cursor: pointer;
    transition: all 0.15s ease;
    min-width: 44px;
  }

  .aspect-pill:hover {
    border-color: var(--color-text-muted);
  }

  .aspect-pill.selected {
    border-color: var(--color-primary);
    background: rgba(255, 27, 141, 0.08);
  }

  .aspect-icon {
    width: 100%;
    max-width: 24px;
    max-height: 28px;
    min-width: 12px;
    min-height: 12px;
    background: var(--color-text-muted);
    border-radius: 2px;
    transition: background 0.15s ease;
  }

  .aspect-pill.selected .aspect-icon {
    background: var(--color-primary);
  }

  .aspect-label {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    font-weight: 500;
  }

  .aspect-pill.selected .aspect-label {
    color: var(--color-primary);
  }

  .aspect-pill:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .aspect-pill:disabled:hover {
    border-color: var(--color-border);
  }

  .resolution-row {
    display: flex;
    gap: 0.5rem;
  }

  .resolution-btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    padding: 0.625rem 0.75rem;
    border: 2px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .resolution-btn:hover {
    border-color: var(--color-text-muted);
  }

  .resolution-btn.selected {
    border-color: var(--color-primary);
    background: rgba(255, 27, 141, 0.08);
  }

  .res-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-text);
  }

  .resolution-btn.selected .res-label {
    color: var(--color-primary);
  }

  .res-hint {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
  }

  .resolution-btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .resolution-btn:disabled:hover {
    border-color: var(--color-border);
  }

  .size-preview-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem;
    background: rgba(0, 0, 0, 0.03);
    border-radius: var(--radius);
  }

  .preview-box-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
  }

  .preview-box {
    background: var(--color-primary);
    border-radius: 3px;
    opacity: 0.9;
    box-shadow: 0 2px 8px rgba(255, 27, 141, 0.3);
  }

  .dimension-display {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    flex: 1;
  }

  .dim-value {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--color-text);
    font-family: var(--font-mono, monospace);
  }

  .swap-btn {
    padding: 0.375rem 0.625rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
    cursor: pointer;
    font-size: 1rem;
    color: var(--color-text-muted);
    transition: all 0.15s ease;
  }

  .swap-btn:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: rgba(255, 27, 141, 0.05);
  }

  .custom-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
    color: var(--color-text);
    cursor: pointer;
  }

  .custom-toggle input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .custom-inputs {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
  }

  .custom-field {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .custom-field label {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .custom-field input {
    width: 100%;
    padding: 0.5rem;
    font-size: 0.875rem;
    font-family: var(--font-mono, monospace);
    border: 1px solid var(--color-border);
    border-radius: var(--radius);
    background: var(--color-surface);
  }

  .custom-field input:focus {
    outline: none;
    border-color: var(--color-primary);
  }

  .dimension-separator {
    font-size: 1rem;
    color: var(--color-text-muted);
    padding-bottom: 0.5rem;
  }

  /* Mobile adjustments for image size section */
  @media (max-width: 640px) {
    .aspect-ratio-row {
      gap: 0.25rem;
    }

    .aspect-pill {
      padding: 0.375rem 0.5rem;
      min-width: 40px;
    }

    .aspect-icon {
      max-width: 20px;
      max-height: 24px;
    }

    .aspect-label {
      font-size: 0.625rem;
    }

    .resolution-btn {
      padding: 0.5rem;
    }

    .res-label {
      font-size: 0.8125rem;
    }

    .size-preview-row {
      padding: 0.625rem;
    }

    .preview-box-container {
      width: 48px;
      height: 48px;
    }

    .dim-value {
      font-size: 0.8125rem;
    }
  }
</style>
